import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Database, ExternalLink, Copy, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';

interface DatabaseErrorDialogProps {
  open: boolean;
  onClose: () => void;
}

export function DatabaseErrorDialog({ open, onClose }: DatabaseErrorDialogProps) {
  const [copied, setCopied] = useState(false);

  const SQL_SCRIPT = `-- Create the key-value store table
CREATE TABLE IF NOT EXISTS kv_store_d4ddf86c (
  key TEXT NOT NULL PRIMARY KEY,
  value JSONB NOT NULL
);

-- Create index for efficient prefix searches
CREATE INDEX IF NOT EXISTS idx_kv_store_key_prefix 
ON kv_store_d4ddf86c (key text_pattern_ops);

-- Verify the table was created
SELECT 'Table created successfully!' as status;`;

  const handleCopy = () => {
    navigator.clipboard.writeText(SQL_SCRIPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-red-500" />
            Database Setup Required
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-left space-y-4">
              <p>
                The database table hasn't been created yet. You need to run a SQL script in your Supabase dashboard to set up the database.
              </p>

              <Alert>
                <AlertDescription>
                  <strong>Don't worry!</strong> This is a one-time setup that takes less than a minute.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <h4 className="font-medium text-foreground">Step 1: Open Supabase SQL Editor</h4>
                <p className="text-sm">
                  Click the button below to open your Supabase SQL Editor in a new tab:
                </p>
                <a 
                  href="https://supabase.com/dashboard/project/lhrqejakjzvxjwhxpynu/sql/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline" className="gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Open SQL Editor
                  </Button>
                </a>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-foreground">Step 2: Copy and Run This SQL</h4>
                <p className="text-sm">
                  Copy the SQL script below, paste it into the SQL Editor, and click "Run":
                </p>
                <div className="relative">
                  <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto border">
                    <code>{SQL_SCRIPT}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2 gap-2"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-3 w-3" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        Copy SQL
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-foreground">Step 3: Refresh This Page</h4>
                <p className="text-sm">
                  After running the SQL script successfully, close this dialog and refresh your browser.
                </p>
              </div>

              <Alert variant="default" className="bg-blue-50 border-blue-200">
                <AlertDescription>
                  <strong>Need help?</strong> Check the DATABASE_SETUP.md file in your project for detailed instructions and troubleshooting tips.
                </AlertDescription>
              </Alert>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>
            I've Set Up the Database
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
